/**
 * ============================================================================
 * DATA LAYER UTILITY CLASS
 * ============================================================================
 */
class DataLayerUtility {
  /**
   * Push to dataLayer with ecommerce reset
   */
  static pushToDataLayer(data) {
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push(data);
  }

  /**
   * Get product data from DOM
   */
  static getProductData() {
    const productElement = document.querySelector('[data-product]');
    if (!productElement) {
      return null;
    }

    try {
      return JSON.parse(productElement.innerHTML);
    } catch (e) {
      console.error('DataLayer: Failed to parse product data', e);
      return null;
    }
  }

  /**
   * Get selected variant data from DOM
   */
  static getSelectedVariantData() {
    const variantElement = document.querySelector('[data-selected-variant]');
    if (!variantElement) {
      return null;
    }

    try {
      return JSON.parse(variantElement.innerHTML);
    } catch (e) {
      console.error('DataLayer: Failed to parse variant data', e);
      return null;
    }
  }

  /**
   * Get list context from section element or URL
   */
  static getListContext(element) {
    const section = element.closest('section, .related-products, featured-products, [data-section-type], [data-item-list-id]');

    if (section) {
      // Prefer explicit data attributes
      if (section.dataset.itemListId && section.dataset.itemListName) {
        return {
          id: section.dataset.itemListId,
          name: section.dataset.itemListName,
        };
      }

      // Fallback to section-based detection
      const sectionId = section.id || section.dataset.sectionId || 'unknown';
      let sectionType = section.dataset.sectionType || section.className.match(/section-([^\s]+)/)?.[1] || 'product_list';

      if (sectionType === 'product_list') {
        const headingElement = section.querySelector('h1, h2, h3');
        sectionType = headingElement ? headingElement?.textContent?.trim() : sectionType;
      }

      return {
        id: sectionId,
        name: sectionType
          .replace(/_/g, ' ')
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase()),
      };
    }

    // Check if on collection page
    if (window.location.pathname.includes('/collections/')) {
      const collectionMatch = window.location.pathname.match(/\/collections\/([^\/\?#]+)/);
      const collectionHandle = collectionMatch ? collectionMatch[1] : 'unknown';
      return {
        id: `collection_${collectionHandle}`,
        name: 'Collection',
      };
    }

    // Check for homepage
    if (window.location.pathname === '/') {
      return {
        id: 'homepage',
        name: 'Homepage',
      };
    }

    return {
      id: 'unknown',
      name: 'Product List',
    };
  }
}

/**
 * ============================================================================
 * DATA LAYER PARENT COMPONENT
 * ============================================================================
 */
class DataLayer extends HTMLElement {
  connectedCallback() {
    // Initialize global dataLayer array once when component mounts
    window.dataLayer = window.dataLayer || [];
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: USER DATA
 * ============================================================================
 */
class DataLayerUserData extends HTMLElement {
  constructor() {
    super();
    this.hasPushed = false;
    this.pollIntervalId = null;
    this.onConsentCollected = this.onConsentCollected.bind(this);
  }

  connectedCallback() {
    // Always listen for consent decisions made during this page view (e.g. the
    // banner re-appearing after a cleared/expired consent cookie, or a
    // preference-center update) - don't rely solely on the one-time check below,
    // since that would leave nothing listening if it happens to short-circuit.
    document.addEventListener('visitorConsentCollected', this.onConsentCollected);
    this.trackUserData();
  }

  disconnectedCallback() {
    document.removeEventListener('visitorConsentCollected', this.onConsentCollected);
    this.stopPolling();
  }

  hasRecordedConsent() {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      const consent = window.Shopify.customerPrivacy.currentVisitorConsent();
      return !!(consent && (consent.analytics === 'yes' || consent.marketing === 'yes'));
    }
    return false;
  }

  trackUserData() {
    if (this.hasRecordedConsent()) {
      this.pushUserData();
      return;
    }

    // Shopify only dispatches `visitorConsentCollected` when consent actually
    // changes, and never retroactively to listeners added after the fact - so
    // if our listener is attached a moment too late relative to the banner's
    // "Accept" click, the event is lost forever. Poll the Customer Privacy API
    // directly as a backstop so a granted consent is never missed, regardless
    // of event timing.
    this.startPolling();
  }

  startPolling() {
    if (this.pollIntervalId) return;
    this.pollIntervalId = setInterval(() => {
      if (this.hasRecordedConsent()) {
        this.pushUserData();
      }
    }, 1000);
  }

  stopPolling() {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  onConsentCollected(event) {
    const detail = event.detail || {};
    if (!(detail.analyticsAllowed || detail.marketingAllowed)) {
      return;
    }
    this.pushUserData();
  }

  pushUserData() {
    if (this.hasPushed) return;

    const userProperties = window.userProperties || null;
    const userData = window.userData || null;

    if (!userProperties) {
      console.warn('DataLayer: user_data_update - Missing user properties');
      return;
    }

    // Only latch the "done" flag once we know we're actually about to push,
    // so a missing-userProperties race can't permanently block a later,
    // legitimate push attempt.
    this.hasPushed = true;
    document.removeEventListener('visitorConsentCollected', this.onConsentCollected);
    this.stopPolling();

    const dataLayerEvent = {
      event: 'user_data_update',
      user_properties: userProperties,
    };

    if (userData && userProperties.logged_in_status === 'member') {
      dataLayerEvent.user_data = userData;
    }

    DataLayerUtility.pushToDataLayer(dataLayerEvent);
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: SELECT ITEM
 * ============================================================================
 */
class DataLayerSelectItem extends HTMLElement {
  constructor() {
    super();
    this.onProductClick = this.onProductClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onProductClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onProductClick);
  }

  onProductClick(event) {
    const productCard = event.target.closest('product-card, .product-card, .custom-product-card');
    if (!productCard) {
      return;
    }

    // Get the link to the product page to check if it's a product page to trigger select_item
    const link = event.target.closest('a[href*="/products/"]');
    if (!link) {
      return;
    }

    // Skip quick-buy buttons (they trigger add_to_cart instead of select_item)
    if (link.closest('.quick-add') || link.closest('form')) {
      return;
    }

    // Deduplication: only track once per product card per page
    if (productCard.hasAttribute('data-gtm-select-tracked')) {
      return;
    }

    const dataElement = productCard.querySelector('[data-product-item-data]');
    if (!dataElement) {
      return;
    }

    try {
      const itemData = JSON.parse(dataElement.innerHTML);
      const listContext = DataLayerUtility.getListContext(productCard);

      // Mark as tracked to prevent duplicate events
      productCard.setAttribute('data-gtm-select-tracked', 'true');

      DataLayerUtility.pushToDataLayer({
        event: 'select_item',
        ecommerce: {
          item_list_id: listContext.id,
          item_list_name: listContext.name,
          items: [itemData],
        },
      });
    } catch (e) {
      console.error('DataLayer: Failed to parse product item data', e);
    }
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: VIEW ITEM LIST
 * ============================================================================
 */
class DataLayerViewItemList extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.mutationObserver = null;

    // Define selectors in one place to prevent mismatch bugs
    this.listSelectors = [
      '.collection-list-content-item',
      '.product-grid',
      'ul.grid',
      '[data-product-list]',
      '.related-products__swiper',
      '.featured-products__swiper',
    ];
  }

  connectedCallback() {
    this.initializeObserver();
    this.observeProductLists();
    this.setupMutationObserver();
  }

  disconnectedCallback() {
    // Disconnect IntersectionObserver
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Disconnect MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  initializeObserver() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      return;
    }

    // Create IntersectionObserver with 25% threshold
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Check if element is intersecting and hasn't been tracked yet
          if (entry.isIntersecting && !entry.target.hasAttribute('data-gtm-list-viewed')) {
            // Mark as viewed to prevent duplicate tracking
            entry.target.setAttribute('data-gtm-list-viewed', 'true');

            // Track the view_item_list event
            this.trackViewItemList(entry.target);

            // Stop observing this element
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.25, // Trigger when 25% of the list is visible
        rootMargin: '0px',
      },
    );
  }

  observeProductLists() {
    if (!this.observer) {
      return;
    }

    // Select all potential product list containers
    const productLists = document.querySelectorAll(this.listSelectors.join(', '));

    productLists.forEach((list) => {
      // Check if list has products (either via data attribute or product cards)
      const hasDataProducts = list.querySelectorAll('[data-product-item-data]').length > 0;
      const hasProductCards =
        list.querySelectorAll('.product-card, .featured-products__card, .related-products__card, .custom-product-card').length > 0;
      const hasProducts = hasDataProducts || hasProductCards;
      const notYetObserved = !list.hasAttribute('data-gtm-observer-attached');

      if (hasProducts && notYetObserved) {
        list.setAttribute('data-gtm-observer-attached', 'true');
        this.observer.observe(list);
      }
    });
  }

  setupMutationObserver() {
    // Check if MutationObserver is supported
    if (!('MutationObserver' in window)) {
      return;
    }

    // Create MutationObserver to watch for dynamically loaded content
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldReobserve = false;

      mutations.forEach((mutation) => {
        // Check if new nodes were added
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            // Check if the added node is an element and contains product lists
            if (node.nodeType === 1) {
              // Check if the added node matches any of the list selectors
              const isMatch = this.listSelectors.some((selector) => node.matches(selector) || node.querySelector(selector));

              // If the added node matches any of the list selectors, re-observe the product lists
              if (isMatch) {
                shouldReobserve = true;
              }
            }
          });
        }
      });

      // Re-scan for product lists if new content was added
      if (shouldReobserve) {
        this.observeProductLists();
      }
    });

    const targetNode = document.getElementById('MainContent') || document.body;

    // Start observing the document for changes
    this.mutationObserver.observe(targetNode, {
      childList: true,
      subtree: true,
    });
  }

  getProductListItems(listElement) {
    // First, try to get products from data-product-item-data elements
    const dataProductCards = listElement.querySelectorAll('[data-product-item-data]');
    if (dataProductCards.length > 0) {
      return Array.from(dataProductCards)
        .map((card) => {
          try {
            return JSON.parse(card.innerHTML);
          } catch (e) {
            console.error('DataLayer: Failed to parse product item', e);
            return null;
          }
        })
        .filter(Boolean);
    }

    // Extract product data from product card elements
    const productCards = listElement.querySelectorAll(
      '.product-card, .featured-products__card, .related-products__card, .custom-product-card',
    );
    return Array.from(productCards)
      .map((card) => {
        try {
          // Try to get product ID from data attribute first
          const productCardElement = card.closest('[data-product-id]') || card.querySelector('[data-product-id]');
          let itemId = productCardElement?.getAttribute('data-product-id');

          // If no data-product-id, extract from URL
          if (!itemId) {
            const link = card.querySelector('a[href*="/products/"]');
            if (!link) return null;

            const productUrl = link.getAttribute('href');
            const productIdMatch = productUrl.match(/\/products\/([^\/\?#]+)/);
            if (!productIdMatch) return null;
            itemId = productIdMatch[1];
          }

          const titleElement = card.querySelector('.product-card__title, h3, h2');
          const title = titleElement?.textContent?.trim() || '';

          const priceElement = card.querySelector('.product-card__price, .price');
          const priceText = priceElement?.textContent?.trim() || '';
          const priceMatch = priceText.match(/[\d,]+\.?\d*/);
          const price = priceMatch ? parseFloat(priceMatch[0].replace(/,/g, '')) * 100 : 0;

          // Build basic product item structure
          return {
            item_id: itemId,
            item_name: title,
            price: price / 100,
            quantity: 1,
          };
        } catch (e) {
          console.error('DataLayer: Failed to extract product data from card', e);
          return null;
        }
      })
      .filter(Boolean);
  }

  trackViewItemList(listElement) {
    const listContext = DataLayerUtility.getListContext(listElement);

    // Get all product items in the list
    const items = this.getProductListItems(listElement);

    if (items.length === 0) {
      console.error('DataLayer: view_item_list - No valid product data found');
      return;
    }

    // Push to dataLayer
    DataLayerUtility.pushToDataLayer({
      event: 'view_item_list',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        item_list_id: listContext.id,
        item_list_name: listContext.name,
        items: items,
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: VIEW ITEM
 * ============================================================================
 */
class DataLayerViewItem extends HTMLElement {
  connectedCallback() {
    this.trackViewItem();
  }

  formatGA4Item(productData, variantData, quantity = 1, sellingPlanId = null) {
    const item = {
      item_id: variantData.sku || variantData.id.toString(),
      item_name: productData.title,
      item_brand: productData.vendor,
      item_category: productData.type,
      item_variant: variantData.title,
      price: variantData.price / 100,
      quantity: quantity,
    };

    if (sellingPlanId) {
      item.selling_plan_id = sellingPlanId;
      item.purchase_type = 'subscription';
    } else {
      item.purchase_type = 'one-time';
    }

    return item;
  }

  trackViewItem() {
    const productData = DataLayerUtility.getProductData();
    const variantData = DataLayerUtility.getSelectedVariantData();

    if (!productData || !variantData) {
      console.error('DataLayer: view_item - Missing product or variant data');
      return;
    }

    const item = this.formatGA4Item(productData, variantData, 1);

    DataLayerUtility.pushToDataLayer({
      event: 'view_item',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: item.price,
        items: [item],
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: ADD TO CART
 * ============================================================================
 */
class DataLayerAddToCart extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
  }

  connectedCallback() {
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  onCartRequestEnd(event) {
    const { requestState } = event.detail || {};

    // Only track successful add requests
    if (requestState?.requestType !== 'add' || !requestState?.responseData?.ok) {
      return;
    }

    // A FormData add returns the line itself; a JSON `items` add (e.g. the landing-page
    // product switcher adding two products at once) returns `{ items: [...] }`.
    const body = requestState?.responseData?.body;
    const lines = Array.isArray(body?.items) ? body.items : [body];
    const items = lines.filter((line) => line?.id).map((line) => this.toItem(line));
    if (!items.length) return;

    const sellingPlanId = items.find((item) => item.selling_plan_id)?.selling_plan_id || null;
    const cartValue = items.reduce((total, item) => total + item.price * item.quantity, 0);

    DataLayerUtility.pushToDataLayer({
      event: 'add_to_cart',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: cartValue,
        cart_value_text: `${cartValue.toFixed(2)}`,
        selling_plan_id: sellingPlanId,
        items,
      },
    });
  }

  /**
   * @param {Object} line - A line item from a /cart/add.js response
   * @returns {Object} GA4 ecommerce item
   */
  toItem(line) {
    const { sku, id, product_title, vendor, product_type, variant_title, price, quantity } = line;
    const sellingPlanId = line.selling_plan_allocation?.selling_plan?.id || null;

    const item = {
      item_id: sku || id.toString(),
      item_name: product_title,
      item_brand: vendor,
      item_category: product_type,
      item_variant: variant_title,
      price: price / 100,
      quantity: quantity,
    };

    if (sellingPlanId) {
      item.selling_plan_id = sellingPlanId;
      item.purchase_type = 'subscription';
    } else {
      item.purchase_type = 'one-time';
    }

    return item;
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: CTA CLICK
 * ============================================================================
 */
class DataLayerCtaClick extends HTMLElement {
  constructor() {
    super();
    this.onButtonClick = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onButtonClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onButtonClick);
  }

  onButtonClick(event) {
    // Skip form inputs (radio, checkbox, input, select, etc.)
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'SELECT' || event.target.tagName === 'TEXTAREA') {
      return;
    }

    // Find the clicked button or link styled as button
    const button = event.target.closest('button, a.button, .button, [class*="button"]');
    if (!button) {
      return;
    }

    // Skip carousel navigation buttons (handled by carousel component)
    if (button.classList.contains('swiper-button-next') || button.classList.contains('swiper-button-prev') || button.closest('.swiper')) {
      return;
    }

    // Skip buttons that are not CTA buttons
    if (
      button.closest('product-card') ||
      button.closest('product-info') ||
      button.closest('.quick-add') ||
      button.closest('form[action*="/cart"]') ||
      button.closest('ajax-cart-product-form') ||
      button.closest('ajax-cart-quantity') ||
      button.type === 'submit' ||
      button.classList.contains('collection-tab') ||
      (button.href && button.href.includes('/cart')) ||
      (button.href && button.href.includes('/checkout'))
    ) {
      return;
    }

    // Get button text and URL
    const linkText = button.textContent?.trim();
    const linkUrl = button.href || window.location.href;
    const ctaLocation = this.getCtaLocation(button);

    DataLayerUtility.pushToDataLayer({
      event: 'cta_click',
      link_text: linkText,
      link_url: linkUrl,
      cta_location: ctaLocation,
    });
  }

  getCtaLocation(button) {
    // First, try to find the nearest heading tag
    let currentElement = button.parentElement;
    while (currentElement && currentElement !== document.body) {
      const heading = currentElement.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading) {
        const headingText = heading.textContent?.trim();
        if (headingText) {
          return headingText;
        }
      }
      currentElement = currentElement.parentElement;
    }

    // Try to find section or container context
    const section = button.closest('section, [data-section-type]');
    if (section) {
      const sectionId = section.id || section.dataset.sectionId || 'unknown-section';
      return sectionId;
    }

    // Check for common locations
    if (button.closest('header')) {
      return 'header';
    }
    if (button.closest('footer')) {
      return 'footer';
    }
    if (button.closest('.hero')) {
      return 'hero';
    }
    if (button.closest('.banner')) {
      return 'banner';
    }

    return 'unidentified-cta-location';
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: FAQ TOGGLE
 * ============================================================================
 */
class DataLayerFaqToggle extends HTMLElement {
  constructor() {
    super();
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.trackedQuestions = new Set();
  }

  connectedCallback() {
    document.addEventListener('click', this.onSummaryClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onSummaryClick, true);
  }

  onSummaryClick(event) {
    const summary = event.target.closest('summary');
    if (summary) {
      const details = summary.parentElement;
      if (details && details.tagName === 'DETAILS') {
        if (details.open) {
          return;
        }

        const faqQuestion = summary.textContent?.trim() || 'FAQ';

        if (this.trackedQuestions.has(faqQuestion)) {
          return;
        }

        this.trackedQuestions.add(faqQuestion);

        DataLayerUtility.pushToDataLayer({
          event: 'faq_toggle',
          faq_question: faqQuestion,
        });
        return;
      }
    }

    const faqButton = event.target.closest('.exclusie-faq-question');
    if (faqButton) {
      if (faqButton.classList.contains('open') || faqButton.getAttribute('aria-expanded') === 'true') {
        return;
      }

      const faqQuestion = faqButton.textContent?.trim() || 'FAQ';

      if (this.trackedQuestions.has(faqQuestion)) {
        return;
      }

      this.trackedQuestions.add(faqQuestion);

      DataLayerUtility.pushToDataLayer({
        event: 'faq_toggle',
        faq_question: faqQuestion,
      });
    }
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: ERROR 404
 * ============================================================================
 */
class DataLayerError404 extends HTMLElement {
  connectedCallback() {
    this.track404();
  }

  track404() {
    DataLayerUtility.pushToDataLayer({
      event: 'error_404',
      page_referrer: document.referrer || 'direct',
      page_location: window.location.href,
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: OUT OF STOCK VIEW
 * ============================================================================
 */
class DataLayerOutOfStock extends HTMLElement {
  connectedCallback() {
    this.trackOutOfStock();
  }

  trackOutOfStock() {
    const productData = DataLayerUtility.getProductData();
    const variantData = DataLayerUtility.getSelectedVariantData();

    if (!productData || !variantData) {
      return;
    }

    if (variantData.available) {
      return;
    }

    DataLayerUtility.pushToDataLayer({
      event: 'out_of_stock_view',
      item_id: variantData.sku || variantData.id.toString(),
      item_name: productData.title,
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: VIEW CART
 * ============================================================================
 */
class DataLayerViewCart extends HTMLElement {
  constructor() {
    super();
    this.onCartClick = this.onCartClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onCartClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onCartClick);
  }

  onCartClick(event) {
    const cartTrigger = event.target.closest('#header-cart-bubble, [data-cart-trigger], .cart-trigger');
    if (cartTrigger) {
      setTimeout(this.trackViewCart.bind(this), 100);
    }
  }

  formatCartItems(cartState) {
    if (!cartState?.items) {
      return [];
    }

    return cartState.items.map((item) => ({
      item_id: item.sku || item.id.toString(),
      item_name: item.product_title,
      item_brand: item.vendor,
      item_category: item.product_type,
      item_variant: item.variant_title,
      price: item.price / 100,
      quantity: item.quantity,
    }));
  }

  trackViewCart() {
    const cartState = window.liquidAjaxCart?.cart;

    if (!cartState || !cartState.items || cartState.item_count === 0) {
      return;
    }

    const items = this.formatCartItems(cartState);
    const cartValue = cartState.total_price / 100;

    DataLayerUtility.pushToDataLayer({
      event: 'view_cart',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: cartValue,
        items: items,
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: REMOVE FROM CART
 * ============================================================================
 */
class DataLayerRemoveFromCart extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
  }

  connectedCallback() {
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  onCartRequestEnd(event) {
    const { requestState } = event.detail || {};

    // Only track change requests that result in removals
    if (requestState?.requestType !== 'change' || !requestState?.responseData?.ok) {
      return;
    }

    const removedItems = requestState.responseData.body?.items_removed;
    const currentCartItems = requestState.responseData.body?.items;

    // Only proceed if there are items removed
    if (!removedItems || removedItems.length === 0) {
      return;
    }

    // Filter to only items that are COMPLETELY removed (not in the current cart)
    const completelyRemovedItems = removedItems.filter((removedItem) => {
      // Check if this item still exists in the cart
      const stillInCart = currentCartItems?.some(
        (cartItem) => cartItem.variant_id === removedItem.variant_id || cartItem.key === removedItem.view_key,
      );
      // Only include if NOT still in cart (completely removed)
      return !stillInCart;
    });

    if (completelyRemovedItems.length > 0) {
      this.trackRemoveFromCart(completelyRemovedItems);
    }
  }

  trackRemoveFromCart(removedItems) {
    let totalRemovedValue = 0;
    const itemsForGA4 = removedItems.map((item) => {
      const itemPrice = parseFloat(item.price) || 0;
      totalRemovedValue += itemPrice * item.quantity;

      return {
        item_id: item.sku || item.variant_id?.toString(),
        item_name: item.product_title,
        item_variant: item.variant_title,
        item_brand: item.vendor,
        price: itemPrice,
        quantity: item.quantity,
        item_category: item.product_type,
      };
    });

    DataLayerUtility.pushToDataLayer({
      event: 'remove_from_cart',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: totalRemovedValue,
        items: itemsForGA4,
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: UPDATE CART
 * ============================================================================
 */
class DataLayerUpdateCart extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
    this.cartStateBeforeUpdate = null;
  }

  connectedCallback() {
    document.addEventListener('liquid-ajax-cart:request-start', (event) => {
      const { requestState } = event.detail || {};
      if (requestState?.requestType === 'change') {
        this.cartStateBeforeUpdate = window.liquidAjaxCart?.cart;
      }
    });

    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  onCartRequestEnd(event) {
    const { requestState } = event.detail || {};

    // Only track change requests
    if (requestState?.requestType !== 'change' || !requestState?.responseData?.ok) {
      return;
    }

    // Get updated items from response
    const updatedItems = requestState.responseData.body?.items;

    // If there are no updated items or the previous cart state is not available, return
    if (!updatedItems || !this.cartStateBeforeUpdate?.items) {
      return;
    }

    // Compare previous cart state with current state to find quantity changes
    updatedItems.forEach((currentItem) => {
      const previousItem = this.cartStateBeforeUpdate.items.find(
        (prev) => prev.key === currentItem.key || prev.variant_id === currentItem.variant_id,
      );

      // If item exists in previous state and quantity changed
      if (previousItem && previousItem.quantity !== currentItem.quantity) {
        DataLayerUtility.pushToDataLayer({
          event: 'update_cart',
          item_id: currentItem.sku || currentItem.variant_id?.toString(),
          item_name: currentItem.product_title,
          previous_quantity: previousItem.quantity,
          new_quantity: currentItem.quantity,
        });
      }
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: SUBSCRIPTION UPSELL
 * ============================================================================
 */
class DataLayerSubscriptionUpsell extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);

    // Load tracked variants from sessionStorage to persist across page loads
    this.trackedVariants = new Set(this.loadFromSessionStorage());
  }

  loadFromSessionStorage() {
    try {
      const stored = sessionStorage.getItem('gtm_subscription_upsell_tracked');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('DataLayer: Failed to load subscription upsell tracking', e);
      return [];
    }
  }

  saveToSessionStorage() {
    try {
      sessionStorage.setItem('gtm_subscription_upsell_tracked', JSON.stringify([...this.trackedVariants]));
    } catch (e) {
      console.error('DataLayer: Failed to save subscription upsell tracking', e);
    }
  }

  connectedCallback() {
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  onCartRequestEnd(event) {
    const { requestState } = event.detail || {};

    // Only track update requests
    if (requestState?.requestType !== 'update' || !requestState?.responseData?.ok) {
      return;
    }

    // Get the first item from response (the one that was updated)
    const updatedItem = requestState.responseData.body?.items?.[0];

    if (!updatedItem || !updatedItem.selling_plan_allocation?.selling_plan?.id) {
      return;
    }

    const variantId = updatedItem.variant_id?.toString();

    // Deduplication: only track once per variant per session
    if (variantId && !this.trackedVariants.has(variantId)) {
      this.trackedVariants.add(variantId);
      this.saveToSessionStorage();
      this.trackSubscriptionUpsell(updatedItem);
    }
  }

  trackSubscriptionUpsell(item) {
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || null;
    const itemPrice = parseFloat(item.price) || 0;
    const itemId = item.sku || item.variant_id?.toString();
    const itemName = item.product_title;

    const ga4Item = {
      item_id: itemId,
      item_name: itemName,
      item_brand: item.vendor,
      item_category: item.product_type,
      item_variant: item.variant_title,
      price: itemPrice / 100,
      quantity: item.quantity,
    };

    if (sellingPlanId) {
      ga4Item.selling_plan_id = sellingPlanId;
      ga4Item.purchase_type = 'subscription';
    } else {
      ga4Item.purchase_type = 'one-time';
    }

    const cartValue = ga4Item.price * ga4Item.quantity;

    DataLayerUtility.pushToDataLayer({
      event: 'subscription_upsell',
      item_id: itemId,
      item_name: itemName,
      upsell_location: 'cart-drawer',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: cartValue,
        items: [ga4Item],
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: PRODUCT OPTION SELECTED
 * ============================================================================
 */
class DataLayerProductOption extends HTMLElement {
  constructor() {
    super();
    this.onOptionChange = this.onOptionChange.bind(this);
    this.onPurchaseOptionOrSellingPlanClick = this.onPurchaseOptionOrSellingPlanClick.bind(this);
    this.lastTrackedEvent = null;
  }

  connectedCallback() {
    document.addEventListener('change', this.onOptionChange);
    document.addEventListener('click', this.onPurchaseOptionOrSellingPlanClick);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onOptionChange);
    document.removeEventListener('click', this.onPurchaseOptionOrSellingPlanClick);
  }

  onOptionChange(event) {
    // Check if change event is from variant-selector
    const variantSelector = event.target.closest('variant-selector');
    if (!variantSelector) {
      return;
    }

    // Determine picker type and extract option data
    const pickerType = variantSelector.dataset.pickerType;
    let optionName, optionValue;

    if (pickerType === 'button') {
      // Extract from radio input
      const input = event.target;
      if (input.type !== 'radio' || !input.checked) {
        return;
      }

      const fieldset = input.closest('fieldset');
      const legend = fieldset?.querySelector('legend');
      optionName = legend?.textContent?.trim() || '';
      optionValue = input.value;
    }

    if (!optionName || !optionValue) {
      return;
    }

    // Get product context
    const productData = DataLayerUtility.getProductData();
    const variantData = DataLayerUtility.getSelectedVariantData();

    if (!productData) {
      return;
    }

    DataLayerUtility.pushToDataLayer({
      event: 'product_option_selected',
      option_name: optionName,
      option_value: optionValue,
      product_id: productData.id?.toString(),
      product_name: productData.title,
      variant_id: variantData?.id?.toString() || '',
      variant_sku: variantData?.sku || '',
    });
  }

  onPurchaseOptionOrSellingPlanClick(event) {
    // Check if click is on purchase option toggle button
    const purchaseBtn = event.target.closest('.purchase-option-btn');
    if (purchaseBtn) {
      const action = purchaseBtn.dataset.action;
      if (!action || (action !== 'one_time' && action !== 'subscription')) {
        return;
      }

      const productData = DataLayerUtility.getProductData();
      if (!productData) {
        return;
      }

      const optionName = purchaseBtn.textContent?.trim() || 'Purchase Option';
      const optionValue = purchaseBtn.dataset.action;

      // Deduplication check
      if (
        this.lastTrackedEvent &&
        this.lastTrackedEvent.name === optionName &&
        this.lastTrackedEvent.value === optionValue &&
        Date.now() - this.lastTrackedEvent.timestamp < 300
      ) {
        return;
      }

      this.lastTrackedEvent = {
        name: optionName,
        value: optionValue,
        timestamp: Date.now(),
      };

      DataLayerUtility.pushToDataLayer({
        event: 'product_option_selected',
        option_name: optionName,
        option_value: optionValue,
        product_id: productData.id?.toString(),
        product_name: productData.title,
        variant_id: productData.variants[0].id?.toString() || '',
        variant_sku: productData.variants[0].sku || '',
      });
      return;
    }

    // Check if click is on selling plan list item or radio input
    const sellingPlanListItem = event.target.closest('[data-selling-plans-list] ul li');

    if (sellingPlanListItem) {
      const productData = DataLayerUtility.getProductData();
      if (!productData) {
        return;
      }

      const sellingPlanName = sellingPlanListItem?.textContent?.trim() || 'Delivery Frequency';

      // Deduplication check
      if (
        this.lastTrackedEvent &&
        this.lastTrackedEvent.name === 'Delivery Frequency' &&
        this.lastTrackedEvent.value === sellingPlanName &&
        Date.now() - this.lastTrackedEvent.timestamp < 300
      ) {
        return;
      }

      this.lastTrackedEvent = {
        name: 'Delivery Frequency',
        value: sellingPlanName,
        timestamp: Date.now(),
      };

      DataLayerUtility.pushToDataLayer({
        event: 'product_option_selected',
        option_name: 'Delivery Frequency',
        option_value: sellingPlanName,
        product_id: productData.id?.toString(),
        product_name: productData.title,
        variant_id: productData.variants[0].id?.toString() || '',
        variant_sku: productData.variants[0].sku || '',
      });
    }
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: CAROUSEL INTERACTION
 * ============================================================================
 */
class DataLayerCarousel extends HTMLElement {
  constructor() {
    super();
    this.onCarouselClick = this.onCarouselClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onCarouselClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onCarouselClick);
  }

  onCarouselClick(event) {
    const button = event.target.closest('div[role="button"], button');
    if (!button) {
      return;
    }

    const isSwiperNext = button.classList.contains('swiper-button-next');
    const isSwiperPrev = button.classList.contains('swiper-button-prev');

    if (isSwiperNext || isSwiperPrev) {
      const swiperContainer = button.closest('.swiper');

      if (swiperContainer) {
        const carouselId = swiperContainer.id || swiperContainer.dataset.carouselId || 'swiper';
        const slideDirection = isSwiperPrev ? 'previous' : 'next';

        DataLayerUtility.pushToDataLayer({
          event: 'carousel_interaction',
          interaction_type: 'arrow_click',
          slide_direction: slideDirection,
          carousel_id: carouselId,
        });
      }
      return;
    }

    const isIngredientsNext = button.classList.contains('next') && button.closest('.controls-buttons');
    const isIngredientsPrev = button.classList.contains('prev') && button.closest('.controls-buttons');

    if (isIngredientsNext || isIngredientsPrev) {
      const carouselContainer = button.closest('wild-roman-carousel') || button.closest('.wild-roman-slider');
      const carouselId = carouselContainer?.id || carouselContainer?.dataset?.carouselId || 'ingredients-slider';
      const slideDirection = isIngredientsPrev ? 'previous' : 'next';

      DataLayerUtility.pushToDataLayer({
        event: 'carousel_interaction',
        interaction_type: 'arrow_click',
        slide_direction: slideDirection,
        carousel_id: carouselId,
      });
    }
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: SELECT CONTENT
 * ============================================================================
 */
class DataLayerSelectContent extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onClick);
    document.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('change', this.onChange);
  }

  onClick(event) {
    // Find element with data-track-content-type
    const element = event.target.closest('[data-track-content-type]');
    if (!element) {
      return;
    }

    // Skip select elements (handled by onChange)
    if (element.tagName === 'SELECT') {
      return;
    }

    // Extract content_type and item_id from data attributes
    const contentType = element.dataset.trackContentType;
    const itemId = element.dataset.trackItemId;
    const itemName = element.dataset.trackItemName || element.textContent?.trim();

    // Validate required attributes
    if (!contentType || !itemId) {
      console.warn('DataLayer: select_content - Missing required data attributes');
      return;
    }

    // Push to dataLayer
    DataLayerUtility.pushToDataLayer({
      event: 'select_content',
      content_type: contentType,
      item_id: itemId,
      item_name: itemName,
    });
  }

  onChange(event) {
    // Handle change events on select elements with data-track-content-type="sort"
    const select = event.target.closest('select[data-track-content-type="sort"]');
    if (!select) {
      return;
    }

    const selectedOption = select.options[select.selectedIndex];
    const itemId = selectedOption.dataset.trackItemId || select.value;
    const itemName = selectedOption.dataset.trackItemName || selectedOption.textContent?.trim();

    // Validate required attributes
    if (!itemId) {
      console.warn('DataLayer: select_content - Missing required data attributes on sort select');
      return;
    }

    // Push to dataLayer
    DataLayerUtility.pushToDataLayer({
      event: 'select_content',
      content_type: 'sort',
      item_id: itemId,
      item_name: itemName,
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: PROMOTION
 * ============================================================================
 */
class DataLayerPromotion extends HTMLElement {
  constructor() {
    super();
    this.onTeaserClick = this.onTeaserClick.bind(this);
    this.observer = null;

    // Load tracked promotions from sessionStorage to persist across page loads
    this.viewedPromotions = new Set(this.loadFromSessionStorage('viewedPromotions'));
    this.selectedPromotions = new Set(this.loadFromSessionStorage('selectedPromotions'));
  }

  loadFromSessionStorage(key) {
    try {
      const stored = sessionStorage.getItem(`gtm_${key}`);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('DataLayer: Failed to load from sessionStorage', e);
      return [];
    }
  }

  saveToSessionStorage(key, set) {
    try {
      sessionStorage.setItem(`gtm_${key}`, JSON.stringify([...set]));
    } catch (e) {
      console.error('DataLayer: Failed to save to sessionStorage', e);
    }
  }

  connectedCallback() {
    document.addEventListener('click', this.onTeaserClick);
    this.scanInitialPopups();
    this.observePromotions();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTeaserClick);
    if (this.observer && this.observer.domObserver) {
      this.observer.domObserver.disconnect();
    }
  }

  onTeaserClick(event) {
    const teaser = event.target.closest('[data-testid="animated-teaser"], .kl-teaser-WrcBWh');
    if (teaser) {
      const promotionName = this.extractPromotionName(teaser) || 'Teaser';

      // Deduplication: only track once per promotion per session
      if (this.selectedPromotions.has(promotionName)) {
        return;
      }
      this.selectedPromotions.add(promotionName);
      this.saveToSessionStorage('selectedPromotions', this.selectedPromotions);

      DataLayerUtility.pushToDataLayer({
        event: 'select_promotion',
        promotion_id: promotionName,
        promotion_name: promotionName,
        creative_name: promotionName,
        items: [
          {
            promotion_id: promotionName,
            promotion_name: promotionName,
            creative_name: promotionName,
          },
        ],
      });
    }
  }

  scanInitialPopups() {
    // Scan for popups that are already visible when component connects
    const existingPopups = document.querySelectorAll('[data-testid="POPUP"]');
    existingPopups.forEach((popup) => {
      if (this.isPopupVisible(popup)) {
        this.handlePopupView(popup);
      }
    });
  }

  isPopupVisible(popup) {
    // Check computed style for display property
    const computedStyle = window.getComputedStyle(popup);
    const display = computedStyle.display;

    // Also check inline style as fallback
    const inlineStyle = popup.style.display;

    // Popup is visible if display is 'flex' or not 'none'
    return display === 'flex' || (display !== 'none' && inlineStyle !== 'none');
  }

  observePromotions() {
    if (!('MutationObserver' in window)) {
      return;
    }

    // Observer for new popup elements being added to DOM
    const domObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const popup = node.matches('[data-testid="POPUP"]') ? node : node.querySelector('[data-testid="POPUP"]');
            if (popup) {
              // Attach style observer to this popup
              this.observePopupStyle(popup);
              // Check if it's already visible
              if (this.isPopupVisible(popup)) {
                this.handlePopupView(popup);
              }
            }
          }
        });
      });
    });

    // Observe DOM for new popups
    domObserver.observe(document.body, { childList: true, subtree: true });

    // Find and observe existing popups
    const existingPopups = document.querySelectorAll('[data-testid="POPUP"]');
    existingPopups.forEach((popup) => {
      this.observePopupStyle(popup);
    });

    // Store the observer
    this.observer = { domObserver };
  }

  observePopupStyle(popup) {
    if (!('MutationObserver' in window)) {
      return;
    }

    // Create a style observer for this specific popup if not already observed
    if (popup.dataset.gtmStyleObserved === 'true') {
      return;
    }

    const styleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          if (this.isPopupVisible(popup)) {
            this.handlePopupView(popup);
          }
        }
      });
    });

    styleObserver.observe(popup, {
      attributes: true,
      attributeFilter: ['style'],
    });

    popup.dataset.gtmStyleObserved = 'true';
  }

  handlePopupView(popup) {
    // Verify popup is actually visible before tracking
    if (!this.isPopupVisible(popup)) {
      return;
    }

    const promotionName = this.extractPromotionName(popup) || 'Popup';

    // Deduplication: only track once per promotion per session
    if (this.viewedPromotions.has(promotionName)) {
      return;
    }
    this.viewedPromotions.add(promotionName);
    this.saveToSessionStorage('viewedPromotions', this.viewedPromotions);

    DataLayerUtility.pushToDataLayer({
      event: 'view_promotion',
      promotion_id: promotionName,
      promotion_name: promotionName,
      creative_name: promotionName,
      items: [
        {
          promotion_id: promotionName,
          promotion_name: promotionName,
          creative_name: promotionName,
        },
      ],
    });
  }

  extractPromotionName(element) {
    const textElement = element.querySelector('h1, h2, h3, p');
    if (textElement && textElement.textContent.trim()) {
      return textElement.textContent.trim();
    }

    return 'Unknown Promotion';
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: LEAD
 * ============================================================================
 */
class DataLayerLead extends HTMLElement {
  constructor() {
    super();
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onButtonClick = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('submit', this.onFormSubmit);
    document.addEventListener('click', this.onButtonClick);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onFormSubmit);
    document.removeEventListener('click', this.onButtonClick);
  }

  onFormSubmit(event) {
    const form = event.target;
    if (this.isNewsletterForm(form)) {
      this.trackLead(form);
    }
  }

  onButtonClick(event) {
    const button = event.target.closest('button');
    if (!button) {
      return;
    }

    // Check if it's a submit-like button for a newsletter form
    const form = button.closest('form');
    if (form && this.isNewsletterForm(form)) {
      // Since Klaviyo often uses AJAX and type="button", we listen for the explicit click
      if (button.textContent.trim().toLowerCase() === 'sign up' || button.type === 'submit') {
        this.trackLead(form);
      }
    }
  }

  isNewsletterForm(form) {
    // Check for Klaviyo forms
    if (form.matches('[class*="klaviyo-form"], [data-testid^="klaviyo-form"]')) {
      return true;
    }

    // Check for native Shopify newsletter forms
    const hasContactAction = form.action && form.action.includes('/contact');
    const formTypeInput = form.querySelector('input[name="form_type"][value="customer"]');
    const newsletterTag = form.querySelector('input[name="contact[tags]"][value="newsletter"]');
    const emailInput = form.querySelector('input[name="contact[email]"]');

    return hasContactAction && formTypeInput && newsletterTag && emailInput;
  }

  trackLead(form) {
    // Try multiple email input selectors to support both Klaviyo and native Shopify forms
    const emailInput = form.querySelector('input[type="email"], input[name="email"], input[name="contact[email]"]');
    const email = emailInput ? emailInput.value : '';

    if (!email) {
      return;
    }

    // Simple deduplication: don't track if we just tracked this email
    if (sessionStorage.getItem('last_lead_email') === email) {
      return;
    }

    sessionStorage.setItem('last_lead_email', email);

    DataLayerUtility.pushToDataLayer({
      event: 'generate_lead',
      currency: window.Shopify?.currency?.active || 'USD',
      value: 0.5,
      lead_type: 'newsletter',
      user_data: {
        email: email,
      },
    });
  }
}

/**
 * ============================================================================
 * COMPONENT REGISTRATION
 * ============================================================================
 */

const dataLayerComponents = {
  'data-layer': DataLayer, // Register parent first
  'data-layer-user-data': DataLayerUserData,
  // Core E-commerce Events
  'data-layer-select-item': DataLayerSelectItem,
  'data-layer-view-item': DataLayerViewItem,
  'data-layer-view-item-list': DataLayerViewItemList,
  'data-layer-add-to-cart': DataLayerAddToCart,
  'data-layer-view-cart': DataLayerViewCart,
  'data-layer-remove-from-cart': DataLayerRemoveFromCart,
  // Cart Operations
  'data-layer-update-cart': DataLayerUpdateCart,
  'data-layer-subscription-upsell': DataLayerSubscriptionUpsell,
  // User Engagement Events
  'data-layer-cta-click': DataLayerCtaClick,
  'data-layer-faq-toggle': DataLayerFaqToggle,
  'data-layer-select-content': DataLayerSelectContent,
  'data-layer-carousel': DataLayerCarousel,
  'data-layer-product-option': DataLayerProductOption,
  'data-layer-promotion': DataLayerPromotion,
  'data-layer-lead': DataLayerLead,
  // System & Status Events
  'data-layer-error-404': DataLayerError404,
  'data-layer-out-of-stock': DataLayerOutOfStock,
};

Object.entries(dataLayerComponents).forEach(([tagName, componentClass]) => {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, componentClass);
  }
});
